import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { 
  Calendar,
  Plus,
  Trash2,
  Save,
  Printer,
  Clock,
  CheckCircle2,
  FileText,
  Users,
  Stethoscope,
  ClipboardList,
  Building,
  GraduationCap,
  Loader2,
  Download,
  FileSpreadsheet
} from "lucide-react";

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const YEARS = [2024, 2025, 2026, 2027];

const ACTIVITY_CATEGORIES = [
  { id: "surveillance", label: "Surveillance", icon: Stethoscope, color: "bg-blue-500" },
  { id: "rounding", label: "Rounding/Audits", icon: ClipboardList, color: "bg-green-500" },
  { id: "education", label: "Education/Training", icon: GraduationCap, color: "bg-purple-500" },
  { id: "committee", label: "Committee/Meeting", icon: Users, color: "bg-amber-500" },
  { id: "reporting", label: "Reporting/Documentation", icon: FileText, color: "bg-cyan-500" },
  { id: "administrative", label: "Administrative", icon: Building, color: "bg-slate-500" },
];

const getDefaultSchedule = () => ({
  activities: [],
  goals: "",
  accomplishments: "",
  challenges: "",
  nextMonthPriorities: "",
  supervisorNotes: "",
});

// Calculate hours between two times
const calculateHours = (startTime, endTime) => {
  if (!startTime || !endTime) return 0;
  const [startH, startM] = startTime.split(':').map(Number);
  const [endH, endM] = endTime.split(':').map(Number);
  const startMinutes = startH * 60 + startM;
  const endMinutes = endH * 60 + endM;
  const diff = endMinutes - startMinutes;
  return diff > 0 ? (diff / 60).toFixed(2) : 0;
};

export default function MySchedule() {
  const currentDate = new Date();
  const [year, setYear] = useState(currentDate.getFullYear());
  const [month, setMonth] = useState(currentDate.getMonth() + 1);
  const [schedule, setSchedule] = useState(getDefaultSchedule());
  const [saving, setSaving] = useState(false);

  // Load schedule from localStorage
  useEffect(() => {
    const key = `schedule_${year}_${month}`;
    const saved = localStorage.getItem(key);
    if (saved) {
      setSchedule(JSON.parse(saved));
    } else {
      setSchedule(getDefaultSchedule());
    }
  }, [year, month]);

  // Save schedule
  const handleSave = () => {
    setSaving(true);
    const key = `schedule_${year}_${month}`;
    localStorage.setItem(key, JSON.stringify(schedule));
    setTimeout(() => {
      setSaving(false);
      toast.success(`Schedule saved for ${MONTHS.find(m => m.value === month)?.label} ${year}`);
    }, 500);
  };

  // Add activity
  const addActivity = () => {
    setSchedule(prev => ({
      ...prev,
      activities: [
        ...prev.activities,
        {
          id: Date.now(),
          date: "",
          category: "surveillance",
          description: "",
          startTime: "08:00",
          endTime: "09:00",
          completed: false
        }
      ]
    }));
  };

  // Remove activity
  const removeActivity = (id) => {
    setSchedule(prev => ({
      ...prev,
      activities: prev.activities.filter(a => a.id !== id)
    }));
  };

  // Update activity
  const updateActivity = (id, field, value) => {
    setSchedule(prev => ({
      ...prev,
      activities: prev.activities.map(a => 
        a.id === id ? { ...a, [field]: value } : a
      )
    }));
  };

  // Update text field
  const updateField = (field, value) => {
    setSchedule(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Export to CSV
  const exportToCSV = () => {
    const monthName = MONTHS.find(m => m.value === month)?.label;
    const headers = ['Date', 'Category', 'Description', 'Start Time', 'End Time', 'Hours', 'Completed'];
    
    const rows = schedule.activities.map(a => {
      const cat = ACTIVITY_CATEGORIES.find(c => c.id === a.category);
      const hours = calculateHours(a.startTime, a.endTime);
      return [
        a.date || '',
        cat?.label || a.category,
        `"${(a.description || '').replace(/"/g, '""')}"`,
        a.startTime || '',
        a.endTime || '',
        hours,
        a.completed ? 'Yes' : 'No'
      ].join(',');
    });

    // Add summary rows
    rows.push('');
    rows.push(`Total Hours,,,,,${totalHours},`);
    rows.push('');
    rows.push('Hours by Category:');
    Object.entries(hoursByCategory).forEach(([cat, hours]) => {
      rows.push(`${cat},,,,,${hours},`);
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `IP_Schedule_${monthName}_${year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Schedule exported to CSV');
  };

  // Print schedule
  const handlePrint = () => {
    window.print();
  };

  // Calculate totals
  const totalHours = schedule.activities.reduce((sum, a) => {
    return sum + parseFloat(calculateHours(a.startTime, a.endTime));
  }, 0).toFixed(2);

  const completedCount = schedule.activities.filter(a => a.completed).length;

  // Hours by category
  const hoursByCategory = schedule.activities.reduce((acc, a) => {
    const cat = ACTIVITY_CATEGORIES.find(c => c.id === a.category);
    const hours = parseFloat(calculateHours(a.startTime, a.endTime));
    const label = cat?.label || a.category;
    acc[label] = (acc[label] || 0) + hours;
    return acc;
  }, {});

  // Format for display
  Object.keys(hoursByCategory).forEach(k => {
    hoursByCategory[k] = hoursByCategory[k].toFixed(2);
  });

  const monthName = MONTHS.find(m => m.value === month)?.label;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 no-print">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            My Schedule
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Track activities with time for supervisor reporting
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="w-32" data-testid="schedule-month-selector">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.map((m) => (
                <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-24" data-testid="schedule-year-selector">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {YEARS.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Print Header */}
      <div className="hidden print:block mb-6">
        <div className="border-b-2 border-[#6A26B5] pb-3">
          <h1 className="text-xl font-bold text-[#6A26B5]">Infection Preventionist Schedule</h1>
          <p className="text-slate-600">Encompass Health Fitchburg • {monthName} {year}</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-[#6A26B5]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-xs">Total Hours</span>
            </div>
            <p className="text-3xl font-bold text-[#6A26B5] font-mono">{totalHours}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-cyan-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <ClipboardList className="w-4 h-4" />
              <span className="text-xs">Activities</span>
            </div>
            <p className="text-3xl font-bold text-cyan-600 font-mono">{schedule.activities.length}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-xs">Completed</span>
            </div>
            <p className="text-3xl font-bold text-emerald-600 font-mono">{completedCount}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <Calendar className="w-4 h-4" />
              <span className="text-xs">Days Logged</span>
            </div>
            <p className="text-3xl font-bold text-amber-600 font-mono">
              {new Set(schedule.activities.map(a => a.date).filter(Boolean)).size}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Hours by Category */}
      {Object.keys(hoursByCategory).length > 0 && (
        <Card className="shadow-sm">
          <CardHeader className="pb-3 bg-slate-50 border-b">
            <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Hours by Category
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {ACTIVITY_CATEGORIES.map(cat => (
                <div key={cat.id} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50">
                  <div className={`w-3 h-3 rounded-full ${cat.color}`}></div>
                  <div>
                    <p className="text-xs text-slate-500">{cat.label}</p>
                    <p className="font-bold font-mono text-slate-900">{hoursByCategory[cat.label] || '0.00'}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Activities List */}
      <Card className="shadow-sm">
        <CardHeader className="pb-3 bg-[#f8f6fc] border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <ClipboardList className="w-5 h-5 text-[#6A26B5]" />
              Time Log
            </CardTitle>
            <Button 
              size="sm" 
              onClick={addActivity}
              className="bg-[#6A26B5] hover:bg-[#5a1f9e] text-white no-print"
              data-testid="add-activity-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Entry
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {schedule.activities.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Clock className="w-16 h-16 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No time entries yet</p>
              <p className="text-sm mt-1">Click "Add Entry" to start logging your activities</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Header row */}
              <div className="hidden md:grid md:grid-cols-12 gap-2 px-3 py-2 bg-slate-100 rounded-lg text-xs font-medium text-slate-600">
                <div className="col-span-1">Done</div>
                <div className="col-span-2">Date</div>
                <div className="col-span-2">Category</div>
                <div className="col-span-3">Description</div>
                <div className="col-span-1">Start</div>
                <div className="col-span-1">End</div>
                <div className="col-span-1">Hours</div>
                <div className="col-span-1"></div>
              </div>
              
              {schedule.activities.map((activity) => {
                const category = ACTIVITY_CATEGORIES.find(c => c.id === activity.category);
                const hours = calculateHours(activity.startTime, activity.endTime);
                
                return (
                  <div 
                    key={activity.id} 
                    className={`grid grid-cols-1 md:grid-cols-12 gap-2 p-3 rounded-lg border ${
                      activity.completed ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'
                    }`}
                  >
                    {/* Checkbox */}
                    <div className="col-span-1 flex items-center">
                      <Checkbox
                        checked={activity.completed}
                        onCheckedChange={(checked) => updateActivity(activity.id, "completed", checked)}
                      />
                    </div>
                    
                    {/* Date */}
                    <div className="col-span-2">
                      <Label className="md:hidden text-xs text-slate-500">Date</Label>
                      <Input
                        type="date"
                        value={activity.date}
                        onChange={(e) => updateActivity(activity.id, "date", e.target.value)}
                        className="h-9 text-sm"
                      />
                    </div>
                    
                    {/* Category */}
                    <div className="col-span-2">
                      <Label className="md:hidden text-xs text-slate-500">Category</Label>
                      <Select 
                        value={activity.category} 
                        onValueChange={(v) => updateActivity(activity.id, "category", v)}
                      >
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ACTIVITY_CATEGORIES.map(cat => (
                            <SelectItem key={cat.id} value={cat.id}>
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${cat.color}`}></div>
                                {cat.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Description */}
                    <div className="col-span-3">
                      <Label className="md:hidden text-xs text-slate-500">Description</Label>
                      <Input
                        placeholder="Activity description"
                        value={activity.description}
                        onChange={(e) => updateActivity(activity.id, "description", e.target.value)}
                        className="h-9 text-sm"
                      />
                    </div>
                    
                    {/* Start Time */}
                    <div className="col-span-1">
                      <Label className="md:hidden text-xs text-slate-500">Start</Label>
                      <Input
                        type="time"
                        value={activity.startTime}
                        onChange={(e) => updateActivity(activity.id, "startTime", e.target.value)}
                        className="h-9 text-sm font-mono"
                      />
                    </div>
                    
                    {/* End Time */}
                    <div className="col-span-1">
                      <Label className="md:hidden text-xs text-slate-500">End</Label>
                      <Input
                        type="time"
                        value={activity.endTime}
                        onChange={(e) => updateActivity(activity.id, "endTime", e.target.value)}
                        className="h-9 text-sm font-mono"
                      />
                    </div>
                    
                    {/* Hours (calculated) */}
                    <div className="col-span-1 flex items-center">
                      <Badge variant="secondary" className="font-mono text-sm">
                        {hours}h
                      </Badge>
                    </div>
                    
                    {/* Delete */}
                    <div className="col-span-1 flex items-center justify-end">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeActivity(activity.id)}
                        className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50 no-print"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
              
              {/* Total row */}
              <div className="grid grid-cols-12 gap-2 px-3 py-3 bg-[#6A26B5] rounded-lg text-white font-medium">
                <div className="col-span-7 md:col-span-9">Total Hours for {monthName}</div>
                <div className="col-span-5 md:col-span-3 text-right font-mono text-xl">{totalHours} hours</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notes Sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="shadow-sm">
          <CardHeader className="pb-3 bg-[#f8f6fc] border-b">
            <CardTitle className="text-sm font-medium text-[#6A26B5]">Monthly Goals</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <Textarea
              placeholder="What are your goals for this month?"
              value={schedule.goals}
              onChange={(e) => updateField("goals", e.target.value)}
              className="min-h-24"
            />
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader className="pb-3 bg-[#f8f6fc] border-b">
            <CardTitle className="text-sm font-medium text-[#6A26B5]">Accomplishments</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <Textarea
              placeholder="What did you accomplish this month?"
              value={schedule.accomplishments}
              onChange={(e) => updateField("accomplishments", e.target.value)}
              className="min-h-24"
            />
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader className="pb-3 bg-[#f8f6fc] border-b">
            <CardTitle className="text-sm font-medium text-[#6A26B5]">Challenges / Barriers</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <Textarea
              placeholder="Any challenges or barriers encountered?"
              value={schedule.challenges}
              onChange={(e) => updateField("challenges", e.target.value)}
              className="min-h-24"
            />
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader className="pb-3 bg-amber-50 border-b border-amber-200">
            <CardTitle className="text-sm font-medium text-amber-700">Notes for Supervisor</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <Textarea
              placeholder="Additional notes for your supervisor..."
              value={schedule.supervisorNotes}
              onChange={(e) => updateField("supervisorNotes", e.target.value)}
              className="min-h-24"
            />
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 justify-end sticky bottom-4 bg-white p-4 rounded-lg border shadow-lg no-print">
        <Button
          variant="outline"
          onClick={handlePrint}
          className="border-slate-300"
          data-testid="print-schedule-btn"
        >
          <Printer className="w-4 h-4 mr-2" />
          Print
        </Button>
        <Button
          variant="outline"
          onClick={exportToCSV}
          className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
          data-testid="export-csv-btn"
        >
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          Export Hours (CSV)
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#6A26B5] hover:bg-[#5a1f9e] text-white px-8"
          data-testid="save-schedule-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Schedule
            </>
          )}
        </Button>
      </div>

      {/* Print Footer */}
      <div className="hidden print:block mt-8 pt-4 border-t">
        <div className="flex justify-between text-sm text-slate-500">
          <span>Total Hours: {totalHours}</span>
          <span>Generated: {new Date().toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );
}
